import { useCallback, useEffect, useMemo, useRef } from 'react'

import { HStack, Text } from '@chakra-ui/react'
import { AutoAwesomeIcon } from '@editor/assets/icons'
import { CursorState } from '@editor/config/constants/cursor'
import { PointArray } from '@editor/interfaces/attribute'
import { LabelProps, PolygonPlaneMeshProps, PolylineProps } from '@editor/interfaces/canvas'
import { TextAnnotationProps } from '@editor/interfaces/canvas/textAnnotation'
import { DrawingParameterTextAnnotation, DrawingType } from '@editor/interfaces/drawing'
import { CanvasConfig, CanvasConfigV2 } from '@editor/interfaces/editor'
import { InspectionItemType } from '@editor/interfaces/inspection'
import { PlaneSide } from '@editor/interfaces/shape'
import { useBlueprintEditorContext } from '@editor/pages/projects/blueprint-editor/contexts/BlueprintEditorContext'
import { BlueprintEditorTools } from '@editor/pages/projects/blueprint-editor/definitions'
import useProjectionPlane from '@editor/pages/projects/blueprint-editor/hooks/useProjectionPlane'
import { addDrawing } from '@editor/pages/projects/blueprint-editor/store/drawings'
import { getDrawingsOfType } from '@editor/services/Drawing'
import { calculatePolylineMidpoint } from '@editor/services/Polyline'
import { alignToZAxis, toMatrix4 } from '@editor/services/Shape'
import { getDistanceLabel, meterToMilimeterWhole, zeroPad } from '@editor/services/Util'
import { useAppDispatch, useAppSelector } from '@editor/store/app'
import { selectInspectionItems } from '@editor/store/selectors'
import { setCursor, setDisableCreate, setHoveredElementId } from '@editor/store/tool'
import polylabel from 'polylabel'
import { Matrix4, Vector2, Vector3 } from 'three'

import { setBatchGenerate, setCanBatchGenerate } from '../../TextAnnotation/store'
import { getLastCount, getNewOrder } from '../../utils'

/**
 * Extra offset added to annotation position to avoid
 * overlapping on the polygon it is annotating.
 * Value in pixels.
 */
const EXTRA_POSITION_OFFSET = 20

const useAutoTextAnnotation = (
  polylines: CanvasConfig,
  surfacePlanes: CanvasConfig,
  volumeEstimation: CanvasConfig,
): CanvasConfigV2 => {
  const { orthographicCamera } = useBlueprintEditorContext()

  // Hooks
  const { projectionDirections, worldToScreen, screenToWorld } = useProjectionPlane(orthographicCamera.current)

  // Store
  const dispatch = useAppDispatch()
  const inspectionItems = useAppSelector(selectInspectionItems)
  const shapes = useAppSelector((state) => state.shapes.shapes)
  const bpDocument = useAppSelector((state) => state.blueprint.drawings.document)
  const drawings = useAppSelector((state) => state.blueprint.drawings.document.drawings)
  const defaultStyles = useAppSelector((state) => state.blueprint.drawings.defaultStyles)
  const batchGenerate = useAppSelector((state) => state.blueprint.tools.textAnnotation.batchGenerate)

  // Refs
  const lastCountRef = useRef<number | null>(null)

  // Vars
  const isTextAnnotationToolSelected = useAppSelector(
    (state) => state.blueprint.editor.selectedTool === BlueprintEditorTools.TextAnnotation,
  )
  const textAnnotations = useMemo(
    () => getDrawingsOfType<DrawingParameterTextAnnotation>(drawings, DrawingType.TextAnnotation, []),
    [drawings],
  )

  /**
   * Generate part name for a shape.
   *
   * @param shapeId The shape ID to find the inspection item for.
   * @param itemType The type of inspection item.
   * @param defaultName The default name to use if no inspection item is found.
   * @returns The part name.
   */
  const getPartName = useCallback(
    (shapeId: string, itemType: InspectionItemType, defaultName: string) =>
      inspectionItems.getByShapeId(shapeId, itemType)?.part_name || defaultName,
    [inspectionItems],
  )

  /**
   * Add part name to polygon mesh props.
   *
   * @param itemType The type of inspection item.
   * @param prefix The prefix for the part name.
   * @param zeroPadded Whether to zero pad the index.
   * @returns A function that adds the part name to the polygon mesh props.
   */
  const addPolygonPartName = useCallback(
    (itemType: InspectionItemType, prefix: string, zeroPadded = true) =>
      (obj: PolygonPlaneMeshProps, index: number): PolygonPlaneMeshProps => ({
        ...obj,
        guideLabel: {
          label: getPartName(
            obj.polygon.shape_id,
            itemType,
            `${prefix}${zeroPadded ? zeroPad(index + 1, 3) : index + 1}`,
          ),
        },
      }),
    [getPartName],
  )

  /**
   * Add part name to polyline mesh props.
   *
   * @param itemType The type of inspection item.
   * @param prefix The prefix for the part name.
   * @param zeroPadded Whether to zero pad the index.
   * @returns A function that adds the part name to the polyline mesh props.
   */
  const addPolylinePartName = useCallback(
    (itemType: InspectionItemType, prefix: string, zeroPadded = true) =>
      (obj: PolylineProps, index: number): PolylineProps => ({
        ...obj,
        label: {
          message:
            inspectionItems.getById(obj.id)?.part_name || `${prefix}${zeroPadded ? zeroPad(index + 1, 3) : index + 1}`,
        },
      }),
    [inspectionItems],
  )

  /**
   * Filter to only upper plane shapes.
   *
   * @param obj The polygon mesh props.
   * @returns Whether the shape is an upper plane.
   */
  const filterUpperPlaneOnly = useCallback(
    (obj: PolygonPlaneMeshProps): boolean => {
      const shape = shapes.polygons.find((s) => s.shape_id === obj.polygon.shape_id)
      if (!shape) return false

      return shape.plane_side === PlaneSide.UPPER
    },
    [shapes],
  )

  /**
   * Common filter to only valid shapes.
   *
   * @param obj The polygon mesh props or distance label props.
   */
  const filterValidShapesOnly = useCallback(
    (obj: PolygonPlaneMeshProps | PolylineProps, customId?: string) =>
      // need to be visible
      (obj.visible === undefined || obj.visible) &&
      (obj.opacity === undefined || obj.opacity > 0) &&
      // not already linked to a text annotation
      !textAnnotations.some(({ drawing_parameters: { leadAnchors } }) =>
        leadAnchors.some((anchor) => anchor.inspectionItemId === (customId || obj.id)),
      ),
    [textAnnotations],
  )

  /**
   * Filter to only polygons that are selected to the blueprint.
   *
   * @param obj The polygon mesh props.
   */
  const filterValidPolygonsOnly = useCallback(
    (obj: PolygonPlaneMeshProps) => {
      let item = inspectionItems.getById(obj.id)
      if (!item) {
        item = inspectionItems.getByShapeId(obj.polygon.shape_id, InspectionItemType.PolygonArea)
      }

      return (
        filterValidShapesOnly(obj, item?.inspection_item_id) &&
        obj.polygon.center &&
        (bpDocument.editorElements.polygons.some((el) => el.id === obj.polygon.shape_id) ||
          bpDocument.editorElements.inspectionItems.some(
            (el) =>
              el.id ===
              inspectionItems.getByShapeId(obj.polygon.shape_id, InspectionItemType.Volume)?.inspection_item_id,
          ))
      )
    },
    [bpDocument, filterValidShapesOnly, inspectionItems],
  )

  /**
   * Filter to only polylines that are selected to the blueprint.
   *
   * @param obj The distance label props.
   */
  const filterValidPolylinesOnly = useCallback(
    (obj: PolylineProps) =>
      filterValidShapesOnly(obj) && bpDocument.editorElements.inspectionItems.some((el) => el.id === obj.id),
    [bpDocument, filterValidShapesOnly],
  )

  /**
   * Collect polygons. Add the names now as they rely on the original index.
   */
  const allPolygons = useMemo(
    () =>
      [
        ...[
          ...(surfacePlanes.objects?.polygonPlaneMeshes || []),
          ...(surfacePlanes.objects?.polygonPlaneMeshesV2 || []),
        ].map(addPolygonPartName(InspectionItemType.PolygonArea, `面積`)),
        ...[
          ...(volumeEstimation.objects?.polygonPlaneMeshes || []),
          ...(volumeEstimation.objects?.polygonPlaneMeshesV2 || []),
        ]
          .filter(filterUpperPlaneOnly)
          .map(addPolygonPartName(InspectionItemType.Volume, `体積`, false)),
      ].filter(filterValidPolygonsOnly),
    [volumeEstimation, surfacePlanes, filterValidPolygonsOnly, filterUpperPlaneOnly, addPolygonPartName],
  )

  /**
   * Collect polylines. Add the names now as they rely on the original index.
   */
  const allPolylines = useMemo(
    () =>
      [
        ...(polylines.objects?.polylines || []).map(
          addPolylinePartName(InspectionItemType.PolylineLength, `延長`, true),
        ),
      ].filter(filterValidPolylinesOnly),
    [polylines, addPolylinePartName, filterValidPolylinesOnly],
  )

  /**
   * Generate common button props for all shapes.
   *
   * @param id ID of the shape. Will be used for all state tracking (hover, click, etc).
   * @param center Center position to place the button at.
   */
  const generateButtonCommonProps = useCallback(
    (id: string, center: Vector3): LabelProps => {
      const label: LabelProps = {
        id,
        point: center.toArray(),
        labelPrefix: '',
        label: (
          <HStack>
            <AutoAwesomeIcon />
            <Text>注釈</Text>
          </HStack>
        ),
        message: '自動で注釈を作成',
        styles: {
          backgroundColor: '{colors.gray2.600}',
          color: 'white',
          padding: [10, 6],
          _hover: { backgroundColor: '{colors.gray2.700}' },
        },
        visible: isTextAnnotationToolSelected,
        onEnter: () => {
          dispatch(setDisableCreate(true))
          setTimeout(() => dispatch(setHoveredElementId(id)))
          dispatch(setCursor(CursorState.POINTER))
        },
        onLeave: () => {
          setTimeout(() => dispatch(setDisableCreate(false)))
          dispatch(setCursor(CursorState.DEFAULT))
        },
      }

      return label
    },
    [isTextAnnotationToolSelected, dispatch],
  )

  /**
   * Generate text annotation for a shape.
   *
   * @param inspectionItemId Inspection Item ID of the shape.
   * @param matrix Transformation matrix for the annotation.
   * @param topText Top text of the annotation.
   * @param bottomText Bottom text of the annotation.
   * @param point Point where the lead anchor will point to.
   */
  const generateTextAnnotation = useCallback(
    (
      inspectionItemId: string,
      matrix: Matrix4,
      topText: TextAnnotationProps['topText'],
      bottomText: TextAnnotationProps['bottomText'],
      point: Vector3,
    ) => {
      // Optional last count to use for naming.
      // If not provided, it will be calculated from existing drawings.
      const itemNumber =
        lastCountRef.current ||
        getLastCount(
          drawings.map((p) => p.name),
          '注釈',
        ) + 1

      const drawing_parameters: DrawingParameterTextAnnotation = {
        uuid: crypto.randomUUID(),
        transformation: matrix.toArray(),
        topText,
        bottomText,
        leadAnchors: [
          {
            id: crypto.randomUUID(),
            point: point.toArray() as PointArray,
            inspectionItemId,
          },
        ],
        order: getNewOrder(drawings),
        styles: defaultStyles,
        width: 200,
      }

      dispatch(
        addDrawing({
          name: `注釈 ${itemNumber}`,
          drawing_type: DrawingType.TextAnnotation,
          drawing_parameters,
        }),
      )
    },
    [drawings, defaultStyles, dispatch],
  )

  /**
   * Generate buttons for polygon-based shapes.
   */
  const allButtons: LabelProps[] = useMemo(() => {
    if (!projectionDirections) return []

    // == Generate buttons for polygons
    const polygonButtons = allPolygons?.map<LabelProps>(({ id, polygon, offsetTransformation, guideLabel }) => {
      // -- Apply offset transformation if available (e.g., from aligned PCDs)
      const offsetMatrix = toMatrix4(offsetTransformation)

      // -- Calculate center of polygon with polylabel using screen positions
      const { points, inverseMatrix, offset } = alignToZAxis(
        polygon.positions.map((v) => new Vector3(...v)),
        true,
      )

      const centered = polylabel([points.map((pos) => [pos.x, pos.y])], 0.001)
      const center = new Vector3(centered[0], centered[1], points[0].z)
        .applyMatrix4(inverseMatrix)
        .add(offset)
        .applyMatrix4(offsetMatrix)

      // -- Generate button
      const button = generateButtonCommonProps(id, center)
      button.onDown = () => {
        // Calculate positions in screen space
        const screenPositions = polygon.positions
          .map((pos) => worldToScreen(new Vector3(...pos).applyMatrix4(offsetMatrix)))
          .filter(Boolean) as Vector2[]
        const centerScreen = worldToScreen(center)

        if (!screenPositions.length || !centerScreen) return

        // Get the maximum x from screen positions
        const maxX = Math.max(...screenPositions.map((p) => p.x))

        // Get a point outside the bounding box for placing the annotation
        const screenOutsidePos = new Vector2(maxX + EXTRA_POSITION_OFFSET, centerScreen.y)
        const outsidePos = screenToWorld(screenOutsidePos)
        if (!outsidePos) return

        const matrix = new Matrix4()
        matrix.setPosition(outsidePos.x, outsidePos.y, outsidePos.z)

        // Define top text
        const topText = (guideLabel?.label || '') as string

        // Define bottom text
        const a = polygon.min_rect_boundaries.extent[0]
        const b = polygon.min_rect_boundaries.extent[1]
        const w = a >= b ? a : b
        const h = a < b ? a : b
        const bottomText = `${meterToMilimeterWhole(w).toFixed(0)}mm × ${meterToMilimeterWhole(h).toFixed(0)}mm`

        // For some reason, surface plane uses shape_id as id but volume estimation uses inspection item id.
        // So we need to find the correct inspection item ID first.
        // And since we don't know what shape this is, let's just do it always and check the result.
        let itemId = id
        const item = inspectionItems.getByShapeId(polygon.shape_id, InspectionItemType.PolygonArea)
        if (item?.inspection_item_id && !item.volume_id) itemId = item.inspection_item_id

        // Generate text annotation
        generateTextAnnotation(itemId, matrix, topText, bottomText, center)
      }

      return button
    })

    const polylineButtons = allPolylines?.map<LabelProps>(({ id, points, offsetTransformation, label }) => {
      // -- Apply offset transformation if available (e.g., from aligned PCDs)
      const offsetMatrix = toMatrix4(offsetTransformation)
      const pointsV3 = points.map((v) => new Vector3(...v).applyMatrix4(offsetMatrix))

      // -- Calculate center point of the polyline
      const { point: center, totalDistance } = calculatePolylineMidpoint(pointsV3)

      // -- Generate button
      const button: LabelProps = generateButtonCommonProps(id, new Vector3().fromArray(center))
      button.onDown = () => {
        // Calculate positions in screen space
        const screenPositions = pointsV3.map((pos) => worldToScreen(pos)).filter(Boolean) as Vector2[]

        // Generate bounding box and get width/height
        const xs = screenPositions.map((p) => p.x)
        const ys = screenPositions.map((p) => p.y)
        const minX = Math.min(...xs)
        const maxX = Math.max(...xs)
        const minY = Math.min(...ys)
        const maxY = Math.max(...ys)
        const boxWidth = maxX - minX
        const boxHeight = maxY - minY

        // If it's a vertical line, place the annotation to the right, else place it above
        const isVertical = boxHeight > boxWidth
        const screenOutsidePos = isVertical
          ? new Vector2(maxX + EXTRA_POSITION_OFFSET, (minY + maxY) / 2)
          : new Vector2((minX + maxX) / 2, minY - EXTRA_POSITION_OFFSET)
        const outsidePos = screenToWorld(screenOutsidePos)
        if (!outsidePos) return

        const matrix = new Matrix4()
        matrix.setPosition(outsidePos.x, outsidePos.y, outsidePos.z)

        // Define top text - whatever is defined in `allPolylines` above.
        const topText = (label && typeof label === 'object' && 'message' in label ? label?.message || '' : '') as string

        // Define bottom text - length of the polyline x width of polyline
        const widthItem = inspectionItems.items.find(
          (item) => item.item_type === InspectionItemType.PolylineWidth && item.polyline_length_id === id,
        )
        const polylineWidth = widthItem?.polyline_width?.estimated_value
        const bottomText = getDistanceLabel(totalDistance, polylineWidth)

        // Generate text annotation
        generateTextAnnotation(id, matrix, topText, bottomText, new Vector3().fromArray(center))
      }

      return button
    })

    return [...polygonButtons, ...polylineButtons]
  }, [
    allPolygons,
    allPolylines,
    projectionDirections,
    inspectionItems,
    screenToWorld,
    worldToScreen,
    generateButtonCommonProps,
    generateTextAnnotation,
  ])

  /**
   * Handle batch generation when triggered.
   * The flag will be triggered in useEditor.
   */
  useEffect(() => {
    if (!batchGenerate) return

    // Initialize last count.
    // When we batch generate, drawings state won't update quickly enough for each iteration,
    // so we need to keep track of the last count ourselves.
    lastCountRef.current = getLastCount(
      drawings.map((p) => p.name),
      '注釈',
    )

    allButtons
      .filter((button) => button.visible)
      .forEach((button) => {
        lastCountRef.current = (lastCountRef.current || 0) + 1
        if (button.onDown) button.onDown({} as React.MouseEvent<HTMLDivElement>)
      })
    dispatch(setBatchGenerate(false))
    lastCountRef.current = null
  }, [allButtons, batchGenerate, drawings, dispatch])

  /**
   * Enable or disable batch generation button based on available shapes.
   */
  useEffect(() => {
    dispatch(setCanBatchGenerate(allButtons.some((button) => button.visible)))
  }, [allButtons, dispatch])

  return {
    objects: {
      labels: [...allButtons],
    },
  }
}

export default useAutoTextAnnotation
